import React from "react";
import { connect } from "react-redux";
import { Input, Select } from "../../Input";
import { Button } from "../../Button";
import language from "../../../language";
import axios from "axios";
import toastMessage from "../../../utils/toastMessage";
import { getStorage } from "../../../utils/storage";
import { Empty } from "../../Empty";
import moment from "moment";
import TESTForm from "../TESTForm";
import { fetchBloodTestQuestionnaire } from "../../../utils/queries/bloodTest/questionnaireQuery";
import accessMenu from "../../../utils/accessMenu";
import { handleValidateMatching } from "../utils";

// Diseases the analyser reports, and which therefore nobody should retype.
//
// HCV is deliberately NOT here. Alinity does send an HCV result, but the
// assay is still being run by hand, so its fields stay open for manual
// entry alongside Syphilis. Move it across when that changes - that is the
// only edit this needs.
// Gray zone counts. It is not a clean screen, so the repeats and the
// confirmation open for it exactly as they do for a reactive one - matching
// on "REACTIVE" alone left a gray zone IS with no follow-up at all.
const isReactiveAnswer = (answer) => {
  const val = String(answer?.value || answer || "")
    .toUpperCase()
    .replace(/[\s_-]/g, "");

  return val === "REACTIVE" || val === "GRAYZONE";
};

// Which disease and which stage a question belongs to. Both separators are
// in use: "HIV/IS" and "SYPHILIS - Notification status".
const FIELD_PATTERNS = [
  { suffix: "/NOTIFICATION STATUS", type: "NOTIFICATION STATUS" },
  { suffix: " - NOTIFICATION STATUS", type: "NOTIFICATION STATUS" },
  { suffix: "/IS", type: "IS" },
  { suffix: "/REPEAT ONE", type: "REPEAT ONE" },
  { suffix: "/REPEAT TWO", type: "REPEAT TWO" },
  { suffix: "/CONFIRMATION", type: "CONFIRMATION" },
  { suffix: " - CONFIRMATION", type: "CONFIRMATION" },
];

const getFieldInfo = (questionText) => {
  const upper = questionText?.toUpperCase()?.trim();
  if (!upper) return null;

  for (const { suffix, type } of FIELD_PATTERNS) {
    if (upper.endsWith(suffix)) {
      // Upper-cased because the same disease is spelled two ways, and
      // grouping on the raw text split Syphilis into two.
      return { disease: upper.slice(0, -suffix.length).trim(), type };
    }
  }

  return null;
};

// A repeat or a confirmation only means anything once the screen came back
// reactive or gray zone. Until then they stay inactive rather than
// inviting an entry that contradicts the screen above them.
const FOLLOW_UP_TYPES = ["REPEAT ONE", "REPEAT TWO", "CONFIRMATION"];

const isNotificationField = (questionText) =>
  getFieldInfo(questionText)?.type === "NOTIFICATION STATUS";

const isFollowUpClosed = (questionText, openDiseases) => {
  const info = getFieldInfo(questionText);

  if (!info || !FOLLOW_UP_TYPES.includes(info.type)) return false;

  return !openDiseases.has(info.disease);
};

const diseasesNeedingFollowUp = (questionnaire = []) => {
  const open = new Set();

  questionnaire.forEach((q) => {
    const info = getFieldInfo(q?.questionText);

    if (info?.type === "IS" && isReactiveAnswer(q.answer)) {
      open.add(info.disease);
    }
  });

  return open;
};

const AUTOMATED_DISEASES = ["HIV", "HBSAG", "HEPATITIS B"];

const isAutomatedField = (questionText) => {
  const text = String(questionText || "").toUpperCase();

  return AUTOMATED_DISEASES.some((disease) => text.includes(disease));
};

const API_URL = process.env.REACT_APP_BBMS_BASE_API;

class Serology extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isSubmitting: false,
      currentTime: moment().format("lll"),
      donation: {},
      questionnaire: [],
      forms: {},
      error: {},
      disabled: true,
      // Results open read-only. Someone opening a record to look at it
      // should not be able to change it by resting a cursor on a dropdown;
      // editing is a deliberate act, behind the Edit button.
      editMode: false,
      showForm: true,
      confirmationModal: false,
      activateDonation: true,
      code: "",
      misMatchFields: [],
      previousTestResults: props.previousTestResults || [],
      user: {},
    };
  }

  componentDidMount = async () => {
    const user = await getStorage();
    this.setState({ user });

    const {
      canCreateBloodTestSerology,
      canConfirmBloodTestSerology,
      canUpdateBloodTestSerology,
// eslint-disable-next-line no-unused-vars
    } = accessMenu();

    // Only count non-cancelled records for code generation
    // so a cancelled test doesn't create a gap in the sequence
    const activeResults = this.state.previousTestResults.filter(
      (r) => r.status !== "cancelled",
    );
    this.setState({
      code: "SR00" + (activeResults.length + 1),
    });

    if (this.props._id) {
      this.setState({
        ...this.props,
      });
    }

    // Manual entry gets its own sequence. ST for a result typed by the
    // lab, S for one the analyser sent, so the two are told apart at a
    // glance on the list - the same split blood grouping already uses.
    //
    // Set after the spread above, which would otherwise carry the
    // analyser's own code across onto the new row.
    if (this.props.isManualEntry) {
      const manual = (this.props.existingCodes || []).filter((code) =>
        String(code).toUpperCase().startsWith("ST"),
      );

      this.setState({ code: `ST00${manual.length + 1}` });
    }

    await this.getQuestionnaires(true);

    if (
      canCreateBloodTestSerology ||
      canConfirmBloodTestSerology ||
      canUpdateBloodTestSerology
    ) {
      this.setState({
        disabled: false,
      });
    } else {
      return this.setState({
        showForm: false,
        disabled: true,
      });
    }
  };

  getQuestionnaires = async (isLoading) => {
    try {
      const { defaultLanguage } = this.props;

      this.setState({ isLoading });

      const { data } = await fetchBloodTestQuestionnaire(defaultLanguage, {
        page: 1,
        limit: 100,
        type: "serology",
      });

// eslint-disable-next-line no-unused-vars
      const modifiedQuestions = [];
// eslint-disable-next-line no-unused-vars

      // data.forEach(el => {
      //   // if (this.props._id) {
      //     // this.setState({
      //     //   ...this.props,
      //     // });

      // })

      if (
        Array.isArray(this.props.questionnaire) &&
        this.props.questionnaire.length > 0
      ) {
        // The backend now stores the full template questionText (e.g.
        // "HBsAg - Hepatitis B Virus/IS") so exact match works directly.
        const modifiedQuestions = data.map((el) => {
          const matchedQuestion = this.props.questionnaire.find(
            (qEl) => qEl.questionText === el.questionText,
          );

          if (matchedQuestion) {
            return { ...el, answer: matchedQuestion.answer };
          }

          return el;
        });

        // Process dependent fields (notification status, repeats, confirmation)
        // for prefilled IS/CONFIRMATION answers — onChangeQuestionnaire only
        // fires on user interaction, not on prefill.
        this.processPrefilledDependents(modifiedQuestions);

        this.setState({ questionnaire: modifiedQuestions, isLoading: false });
        return;
      }

      this.setState({
        questionnaire: data,
        isLoading: false,
      });
    } catch (error) {
      console.log(error);
      this.setState({
        isLoading: false,
      });
    }
  };

  processPrefilledDependents = (questionnaire) => {
    // Group questions by disease
    const diseaseGroups = {};
    questionnaire.forEach((q) => {
      if (!q.questionText) return;
      const info = getFieldInfo(q.questionText);
      if (info) {
        if (!diseaseGroups[info.disease]) {
          diseaseGroups[info.disease] = {};
        }
        diseaseGroups[info.disease][info.type] = q;
      }
    });

    // Process each disease group
    Object.values(diseaseGroups).forEach((group) => {
      const isQuestion = group["IS"];
      const confirmationQuestion = group["CONFIRMATION"];
      const notificationQuestion = group["NOTIFICATION STATUS"];
      const repeatOne = group["REPEAT ONE"];
      const repeatTwo = group["REPEAT TWO"];

      // Derived on the server, shown here. Never required, because nobody
      // fills it in - leaving it required blocked submit on a field the
      // user cannot touch.
      if (notificationQuestion) {
        notificationQuestion.disabled = true;
        notificationQuestion.required = false;
        delete notificationQuestion.error;
      }

      // Process IS answer (controls visibility of dependent fields)
      if (isQuestion && isQuestion.answer) {
        const answerValue = isQuestion.answer;
        const reactive = isReactiveAnswer(answerValue);

        // The notification status is worked out on the server, from every
        // stage at once, and can read "Reactive (Confirmation Pending)" or
        // "Non Reactive (IS Reactive)". Copying the IS answer over it here
        // flattened it back to a bare Reactive or Non Reactive on every
        // reopen, so those values were written and never seen.
        //
        // It is shown, not set: read-only, never required, and whatever the
        // server last stored.
        if (notificationQuestion) {
          notificationQuestion.disabled = true;
          notificationQuestion.required = false;
          delete notificationQuestion.error;

          if (reactive) {
            delete notificationQuestion?.settings?.hide;
          }
        }

        if (reactive) {
          if (repeatOne) {
            repeatOne.required = true;
            delete repeatOne.settings?.hide;
          }
          if (repeatTwo) {
            repeatTwo.required = true;
            delete repeatTwo.settings?.hide;
          }
          if (confirmationQuestion) {
            delete confirmationQuestion.settings?.hide;
          }
        } else {
          if (repeatOne) {
            repeatOne.required = false;
            if (repeatOne.settings) repeatOne.settings.hide = true;
            delete repeatOne.error;
          }
          if (repeatTwo) {
            repeatTwo.required = false;
            if (repeatTwo.settings) repeatTwo.settings.hide = true;
            delete repeatTwo.error;
          }
          if (confirmationQuestion) {
            confirmationQuestion.required = false;
            if (confirmationQuestion.settings)
              confirmationQuestion.settings.hide = true;
            delete confirmationQuestion.error;
          }
        }
      }

      // A confirmation used to overwrite the notification status too. Same
      // reason as above: the server already accounts for it, and a bare
      // "Non Reactive" here hid that a repeat had been reactive.
      if (confirmationQuestion && confirmationQuestion.answer) {
        if (notificationQuestion) {
          notificationQuestion.disabled = true;
          notificationQuestion.required = false;
        }
      }
    });
  };

  onChangeQuestionnaire = async ({ value, index }) => {
    let { questionnaire } = this.state;

    let question = questionnaire[index];

    let answerValue = value?.target ? value?.target?.value : value;

    questionnaire[index]["answer"] = answerValue;

    delete questionnaire[index]["error"];

    const answerValueValue = answerValue?.value || answerValue;

    handleValidateMatching({
      that: this,
      field: questionnaire[index]?.questionText,
      value: answerValueValue,
      questionnaireIndex: index,
    });

    if (
      question?.settings &&
      question?.settings?.[answerValueValue] &&
      Object.keys(question?.settings?.[answerValueValue])?.includes(
        "activateDonation",
      )
    ) {
      this.setState({
        activateDonation:
          question?.settings?.[answerValueValue]?.activateDonation,
      });
    }

    if (question?.questionText?.toUpperCase() === "HIV/CONFIRMATION") {
      questionnaire.forEach((el) => {
        if (
          el.questionText?.toUpperCase()?.trim() === "HIV/NOTIFICATION STATUS"
        ) {
          el.answer = answerValue;
          el.disabled = true;
        }
      });
    }

    if (question?.questionText?.toUpperCase()?.trim() === "HIV/IS") {
      questionnaire.forEach((el) => {
        if (
          el.questionText?.toUpperCase()?.trim() === "HIV/NOTIFICATION STATUS"
        ) {
          el.answer = answerValue;
          el.disabled = true;
        }

        if (answerValue?.value?.toUpperCase()?.trim() === "REACTIVE") {
          if (el.questionText?.toUpperCase()?.trim() === "HIV/REPEAT ONE") {
            el.required = true;
            delete el.settings.hide;
          }

          if (el.questionText?.toUpperCase()?.trim() === "HIV/REPEAT TWO") {
            el.required = true;
            delete el.settings.hide;
          }

          // if (el.questionText?.toUpperCase()?.trim() === "HIV/CONFIRMATION") {
          //   el.required = true;
          //   delete el.settings.hide;
          // }

          if (
            el.questionText?.toUpperCase()?.trim() === "HIV/NOTIFICATION STATUS"
          ) {
            el.required = true;
            delete el?.settings?.hide;
          }
        } else {
          if (el.questionText?.toUpperCase()?.trim() === "HIV/REPEAT ONE") {
            el.required = false;
            el.settings.hide = true;
            delete el.error;
          }

          if (el.questionText?.toUpperCase()?.trim() === "HIV/REPEAT TWO") {
            el.required = false;
            el.settings.hide = true;
            delete el.error;
          }

          if (el.questionText?.toUpperCase()?.trim() === "HIV/CONFIRMATION") {
            el.required = false;
            el.settings.hide = true;
            delete el.error;
          }
        }
      });
    }

    if (
      question?.questionText?.toUpperCase() ===
      "HBSAG - HEPATITIS B VIRUS/CONFIRMATION"
    ) {
      questionnaire.forEach((el) => {
        if (
          el.questionText?.toUpperCase()?.trim() ===
          "HBSAG - HEPATITIS B VIRUS/NOTIFICATION STATUS"
        ) {
          el.answer = answerValue;
          el.disabled = true;
        }
      });
    }

    if (
      question?.questionText?.toUpperCase() === "HBSAG - HEPATITIS B VIRUS/IS"
    ) {
      questionnaire.forEach((el) => {
        if (
          el.questionText?.toUpperCase()?.trim() ===
          "HBSAG - HEPATITIS B VIRUS/NOTIFICATION STATUS"
        ) {
          el.answer = answerValue;
          el.disabled = true;
        }

        if (answerValue?.value?.toUpperCase()?.trim() === "REACTIVE") {
          if (
            el.questionText?.toUpperCase()?.trim() ===
            "HBSAG - HEPATITIS B VIRUS/REPEAT ONE"
          ) {
            el.required = true;
            el.settings.hide = false;
          }

          if (
            el.questionText?.toUpperCase()?.trim() ===
            "HBSAG - HEPATITIS B VIRUS/REPEAT TWO"
          ) {
            el.required = true;
            el.settings.hide = false;
          }

          if (
            el.questionText?.toUpperCase()?.trim() ===
            "HBSAG - HEPATITIS B VIRUS/CONFIRMATION"
          ) {
            el.required = true;
            el.settings.hide = false;
          }

          if (
            el.questionText?.toUpperCase()?.trim() ===
            "HBSAG - HEPATITIS B VIRUS/NOTIFICATION STATUS"
          ) {
            el.required = true;
            delete el?.settings?.hide;
          }
        } else {
          if (
            el.questionText?.toUpperCase()?.trim() ===
            "HBSAG - HEPATITIS B VIRUS/REPEAT ONE"
          ) {
            el.required = false;
            el.settings.hide = true;
            delete el.error;
          }

          if (
            el.questionText?.toUpperCase()?.trim() ===
            "HBSAG - HEPATITIS B VIRUS/REPEAT TWO"
          ) {
            el.required = false;
            el.settings.hide = true;
            delete el.error;
          }

          if (
            el.questionText?.toUpperCase()?.trim() ===
            "HBSAG - HEPATITIS B VIRUS/CONFIRMATION"
          ) {
            el.required = false;
            el.settings.hide = true;
            delete el.error;
          }

          if (
            el.questionText?.toUpperCase()?.trim() ===
            "HBSAG - HEPATITIS B VIRUS/NOTIFICATION STATUS"
          ) {
            el.required = false;
            el.settings.hide = true;
            delete el.error;
          }
        }
      });
    }

    if (
      question?.questionText?.toUpperCase()?.trim() ===
      "HCV - HEPATITIS C VIRUS/CONFIRMATION"
    ) {
      questionnaire.forEach((el) => {
        if (
          el.questionText?.toUpperCase()?.trim() ===
          "HCV - HEPATITIS C VIRUS/NOTIFICATION STATUS"
        ) {
          el.answer = answerValue;
          el.disabled = true;
        }
      });
    }

    if (
      question?.questionText?.toUpperCase()?.trim() ===
      "HCV - HEPATITIS C VIRUS/IS"
    ) {
      questionnaire.forEach((el) => {
        if (
          el.questionText?.toUpperCase()?.trim() ===
          "HCV - HEPATITIS C VIRUS/NOTIFICATION STATUS"
        ) {
          el.answer = answerValue;
          el.disabled = true;
        }

        if (answerValue?.value?.toUpperCase()?.trim() === "REACTIVE") {
          if (
            el.questionText?.toUpperCase()?.trim() ===
            "HCV - HEPATITIS C VIRUS/REPEAT ONE"
          ) {
            el.required = true;
            el.settings.hide = false;
          }

          if (
            el.questionText?.toUpperCase()?.trim() ===
            "HCV - HEPATITIS C VIRUS/REPEAT TWO"
          ) {
            el.required = true;
            el.settings.hide = false;
          }

          if (
            el.questionText?.toUpperCase()?.trim() ===
            "HCV - HEPATITIS C VIRUS/CONFIRMATION"
          ) {
            // el.required = true;
            el.settings.hide = false;
          }

          if (
            el.questionText?.toUpperCase()?.trim() ===
            "HCV - HEPATITIS C VIRUS/NOTIFICATION STATUS"
          ) {
            el.required = true;
            delete el?.settings?.hide;
          }
        } else {
          if (
            el.questionText?.toUpperCase()?.trim() ===
            "HCV - HEPATITIS C VIRUS/REPEAT ONE"
          ) {
            el.required = false;
            el.settings.hide = true;
            delete el.error;
          }

          if (
            el.questionText?.toUpperCase()?.trim() ===
            "HCV - HEPATITIS C VIRUS/REPEAT TWO"
          ) {
            el.required = false;
            el.settings.hide = true;
            delete el.error;
          }

          if (
            el.questionText?.toUpperCase()?.trim() ===
            "HCV - HEPATITIS C VIRUS/CONFIRMATION"
          ) {
            // el.required = false;
            el.settings.hide = true;
            delete el.error;
          }

          if (
            el.questionText?.toUpperCase()?.trim() ===
            "HCV - HEPATITIS C VIRUS/NOTIFICATION STATUS"
          ) {
            el.required = false;
            el.settings.hide = true;
            delete el.error;
          }
        }
      });
    }

    if (
      question?.questionText?.toUpperCase()?.trim() ===
      "SYPHILIS - CONFIRMATION"
    ) {
      questionnaire.forEach((el) => {
        if (
          el.questionText?.toUpperCase()?.trim() ===
          "SYPHILIS - NOTIFICATION STATUS"
        ) {
          el.answer = answerValue;
          el.disabled = true;
        }
      });
    }

    if (question?.questionText?.toUpperCase() === "SYPHILIS/IS") {
      questionnaire.forEach((el) => {
        if (
          el.questionText?.toUpperCase()?.trim() ===
          "SYPHILIS - NOTIFICATION STATUS"
        ) {
          el.answer = answerValue;
          el.disabled = true;
        }

        if (answerValue?.value?.toUpperCase()?.trim() === "REACTIVE") {
          if (
            el.questionText?.toUpperCase()?.trim() === "SYPHILIS/REPEAT ONE"
          ) {
            el.required = true;
            el.settings.hide = false;
          }

          if (
            el.questionText?.toUpperCase()?.trim() === "SYPHILIS/REPEAT TWO"
          ) {
            el.required = true;
            el.settings.hide = false;
          }

          if (
            el.questionText?.toUpperCase()?.trim() === "SYPHILIS - CONFIRMATION"
          ) {
            // el.required = true;
            el.settings.hide = false;
          }

          if (
            el.questionText?.toUpperCase()?.trim() ===
            "SYPHILIS - NOTIFICATION STATUS"
          ) {
            if (
              this.props?.sampleCollectionMethod?.toUpperCase() === "ALINITY"
            ) {
              el.required = true;
              delete el?.settings?.hide;
            }
          }
        } else {
          if (
            el.questionText?.toUpperCase()?.trim() === "SYPHILIS/REPEAT ONE"
          ) {
            el.required = false;
            el.settings.hide = true;
            delete el.error;
          }

          if (
            el.questionText?.toUpperCase()?.trim() === "SYPHILIS/REPEAT TWO"
          ) {
            el.required = false;
            el.settings.hide = true;
            delete el.error;
          }

          if (
            el.questionText?.toUpperCase()?.trim() === "SYPHILIS - CONFIRMATION"
          ) {
            // el.required = false;
            el.settings.hide = true;
            delete el.error;
          }

          if (
            el.questionText?.toUpperCase()?.trim() ===
            "SYPHILIS - NOTIFICATION STATUS"
          ) {
            if (
              this.props?.sampleCollectionMethod?.toUpperCase() === "ALINITY"
            ) {
              el.required = false;
              el.settings.hide = true;
              delete el.error;
            }
          }
        }
      });
    }

    // When Repeat Two changes, auto-update Notification Status to match.
    // If Confirmation is later filled it will override this value.
    const repeatTwoNotifMap = {
      "HCV - HEPATITIS C VIRUS/REPEAT TWO": "HCV - HEPATITIS C VIRUS/NOTIFICATION STATUS",
      "HIV/REPEAT TWO": "HIV/NOTIFICATION STATUS",
      "HBSAG - HEPATITIS B VIRUS/REPEAT TWO": "HBSAG - HEPATITIS B VIRUS/NOTIFICATION STATUS",
      "SYPHILIS/REPEAT TWO": "SYPHILIS - NOTIFICATION STATUS",
    };
    const repeatTwoKey = question?.questionText?.toUpperCase()?.trim();
    const notifTarget = repeatTwoNotifMap[repeatTwoKey];
    if (notifTarget) {
      questionnaire.forEach((el) => {
        if (el.questionText?.toUpperCase()?.trim() === notifTarget) {
          el.answer = answerValue;
          el.disabled = true;
        }
      });
    }

    this.setState({
      questionnaire,
    });
  };

  onChangeText(field, e) {
    let { error } = this.state;

    delete error[field];

    this.setState({
      [field]: e && e?.target ? e?.target?.value : e,
      error,
    });
  }

  validateForm() {
// eslint-disable-next-line no-unused-vars
    let { questionnaire, error, code, bloodGroup } = this.state;
// eslint-disable-next-line no-unused-vars

    questionnaire.forEach((el) => {
      if (el.required && (!el.answer || el.answer === "")) {
        el.error = `${el.questionText} ${
          language[this.props.defaultLanguage].is_required
        }`;
      }
    });

    if (code === "") {
      error.code = language[this.props.defaultLanguage].code_required;
    }

    this.setState({ questionnaire, error });
  }

  onSubmit = async (status) => {
    const { questionnaire, error, code } = this.state;

    const { testId } = this.props;

    await this.validateForm();

    let questionnaireError = questionnaire
      .map((el) => {
        if (el.error) {
          return el.error;
        }
        return null;
      })
      .filter((error) => error !== undefined && error !== null);

    if (Object.keys(error).length > 0) return;

    if (questionnaireError.length > 0) return;

    this.setState({
      isSubmitting: true,
    });

    await this.handleUpdateQuestionnaire();

    let url = `${API_URL}/bloodTestHistory`,
      method = "POST",
      user = await getStorage();
    let { _id } = this.props;

    let requestedBody = {
      testBy: user.id,
      bloodTest: testId,
      questionnaireType: "serology",
      questionnaire,
      code,
    };

    // A manual entry is a new record even though the form was opened on
    // the analyser's row. Without this it saved as a PUT and overwrote the
    // results Alinity had sent.
    if (_id && _id !== "" && !this.props.isManualEntry) {
      method = "PUT";
      requestedBody.id = _id;
      delete requestedBody.testBy;
    }

    if (status) {
      requestedBody.status = status;
    }

    const options = {
      method,
      url,
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + user.token,
      },
      data: requestedBody,
    };

    axios(options)
      .then(async (data) => {
        this.setState({
          isSubmitting: false,
          // Saved, so lock the fields again. Leaving them open invites a
          // second, unintended edit on a record that reads as finished.
          editMode: false,
        });

        if (this.props.canConfirm) {
          await this.handleValidation();
        }

        toastMessage(
          "success",
          language[this.props.defaultLanguage][
            method === "PUT"
              ? "update_bloodtest_success"
              : "add_bloodtest_success"
          ],
        );

        this.props.getData && this.props.getData();
        this.props.handleCloseModal && this.props.handleCloseModal();
      })
      .catch((error) => {
        this.setState({ isSubmitting: false });
        toastMessage("error", error);
      });
  };

  handleOpenModal(modal) {
    this.setState({
      [modal]: true,
    });
  }

  handleCloseModal(modal) {
    this.setState({
      [modal]: false,
    });
  }

  handleValidation = async () => {
    try {
      const { questionnaire, code, error } = this.state;

      await this.validateForm();

      if (Object.keys(error).length > 0) return;

      this.setState({ isValidating: true });

      const { testId } = this.props;

      const user = await getStorage();

      let requestedBody = {
        bloodTestId: testId,
        type: "serology",
        questionnaire,
        code,
      };

      const options = {
        method: "POST",
        url: `${API_URL}/bloodTest/comfirmTest`,
        data: requestedBody,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + user.token,
        },
      };

      axios(options).then((data) => {
        this.setState({
          isValidating: false,
        });

        toastMessage(
          "success",
          language[this.props.defaultLanguage].confirm_blood_test,
        );
        this.props.getData && this.props.getData();
        this.props.handleCloseModal && this.props.handleCloseModal();
      });
    } catch (error) {
      this.setState({
        isValidating: false,
      });

      toastMessage(
        "error",
        language[this.props.defaultLanguage].confirm_blood_test_not_same,
      );
    }
  };

  handleUpdateQuestionnaire = async () => {
    for (let el of this.state.previousTestResults) {
      try {
        let url = `${API_URL}/bloodTestHistory`,
          user = await getStorage();

        let requestedBody = {
          bloodGroup: el.bloodGroup?.value || el.bloodGroup,
          questionnaire: el.questionnaire,
          id: el._id,
          bloodTest: el.bloodTest._id,
        };

        delete requestedBody._id;

        return await axios({
          url,
          method: "PUT",
          data: requestedBody,
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        });
      } catch (error) {
        console.log(error);
      }
    }
  };

  renderChangePrevEntry({ field, questionnaireIndex, type, options }) {
    if (this.state.previousTestResults.length > 0) {
      return this.state.previousTestResults?.map((el, i) => {
        if (el.status === "cancelled") return null;
        return this.renderChangePrevEntryInput({
          label: el.code,
          field,
          questionnaireIndex,
          type,
          options,
          testIndex: i,
        });
      });
    }
  }

  renderChangePrevEntryInput({
    type,
    options,
    field,
    testIndex,
    questionnaireIndex,
    label,
  }) {
    let value;

    if (questionnaireIndex !== undefined && testIndex !== undefined) {
      value =
        this.props?.previousTestResults[testIndex].questionnaire[
          questionnaireIndex
        ].answer;
    } else if (testIndex !== undefined) {
      value = this.props?.previousTestResults[testIndex][field];
    }

    if (type === "dropdown") {
      return (
        <Select
          key={testIndex}
          options={options}
          label={label + "-ENTRY-" + field}
          value={
            typeof value === "string"
              ? options.map((el) => el.value === value && el)
              : value
          }
          onChange={(e) =>
            this.handleChangePrevEntry({
              field,
              index: testIndex,
              value: e && e?.target ? e?.target?.value : e,
              questionnaireIndex,
            })
          }
        />
      );
    }

    return (
      <Input
        label={label + "-ENTRY-" + field}
        value={value}
        onChange={(e) =>
          this.handleChangePrevEntry({
            field,
            index: testIndex,
            value: e && e?.target ? e?.target?.value : e,
            questionnaireIndex,
          })
        }
      />
    );
  }

  handleChangePrevEntry({ index, field, questionnaireIndex, value }) {
    const previousTestResults = [...this.state.previousTestResults];
    if (questionnaireIndex !== undefined) {
      const questionnaire = [...(this.state.questionnaire || [])];
      questionnaire[questionnaireIndex] = { ...questionnaire[questionnaireIndex], answer: value };
      delete questionnaire[questionnaireIndex]["error"];
      previousTestResults[index]["questionnaire"][questionnaireIndex] = questionnaire[questionnaireIndex];

      this.setState({ previousTestResults, questionnaire }, () => {
        handleValidateMatching({
          that: this,
          field,
          value: value?.value || value,
          questionnaireIndex,
        });
      });
    } else {
      previousTestResults[index][field] = value;

      this.setState({ previousTestResults }, () => {
        handleValidateMatching({ that: this, field, value: this.state[field] });
      });
    }
  }

  render() {
    if (!this.state.showForm)
      return (
        <Empty
          title={language[this.props.defaultLanguage].no_access}
          icon="help"
          description={
            language[this.props.defaultLanguage].no_access_description
          }
        />
      );

    const isSuperAdmin = this.state?.user?.role?.toUpperCase() === "SUPERADMIN";

    const { canSerologyOverrideAlinityTestRes } = accessMenu();
    const canEdit = canSerologyOverrideAlinityTestRes || isSuperAdmin;

    // Locking applies to a record that already exists. A new entry has
    // nothing to protect, and gating it behind a button that only an
    // override account ever sees left ordinary lab staff unable to type a
    // result at all - which is what stopped Syphilis being entered.
    const isExisting = Boolean(this.props._id);
    // Add on a sample the analyser already reported reopens that record, so
    // the manual results land beside the automatic ones rather than in a
    // second entry nobody reads.
    // Diseases whose screen came back reactive or gray zone. Only these
    // may have a repeat or a confirmation entered.
    const openForFollowUp = diseasesNeedingFollowUp(this.state.questionnaire);

    const isAlinityRecord =
      this.props?.sampleCollectionMethod === "alinity" || Boolean(isExisting);

    return (
      <div>
        <div className="row">
          <div className="col-md-12">
            {/* pe-3 keeps the button off the modal edge, which it was
                touching. */}
            {canEdit && isAlinityRecord && (
              <div className="d-flex justify-content-end mb-2 pe-3">
                <Button
                  text={
                    this.state.editMode
                      ? language[this.props.defaultLanguage].cancel
                      : language[this.props.defaultLanguage].edit
                  }
                  className={
                    this.state.editMode ? "btn-default" : "btn-outline-primary"
                  }
                  onPress={() =>
                    this.setState({ editMode: !this.state.editMode })
                  }
                />
              </div>
            )}
            <div className="mb-3">
              <div className="card mb-2">
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-12">
                      <Input
                        label={language[this.props.defaultLanguage].code}
                        placeholder={
                          language[this.props.defaultLanguage].code_placeholder
                        }
                        value={this.state?.code}
                        error={this.state.error.code}
                        onChange={(e) => this.onChangeText("code", e)}
                        disabled={
                          this.props?.sampleCollectionMethod === "alinity" ||
                          canSerologyOverrideAlinityTestRes
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="card-body">
              <div className="row">
                {this.state.questionnaire.map((form, index) => {
                  const options = form?.options?.map((el) => {
                    return {
                      label: el,
                      value: el,
                    };
                  });

                  return (
                    <div className="col-md-4" key={index}>
                      <TESTForm
                        label={form.questionText}
                        error={form.error}
                        {...form}
                        options={form?.options?.map((el) => {
                          return {
                            label: el,
                            value: el,
                          };
                        })}
                        onChange={(e) =>
                          this.onChangeQuestionnaire({
                            index,
                            form,
                            value: e,
                          })
                        }
                        disabled={
                          // Only the analyser's own results are protected.
                          // The manual ones - HCV and Syphilis - stay open
                          // so they can be added on this same record, which
                          // is how they reach the modal the lab reads.
                          (isAlinityRecord &&
                            isAutomatedField(form.questionText) &&
                            !(canEdit && this.state.editMode)) ||
                          // A repeat or a confirmation stays inactive until
                          // its own screen comes back reactive or gray
                          // zone; a non-reactive screen needs neither.
                          isFollowUpClosed(form.questionText, openForFollowUp) ||
                          // Derived for the analyser's own diseases: it
                          // follows from the IS, repeat and confirmation
                          // answers, so the way to change it is to correct
                          // one of those. HCV and Syphilis are still
                          // entered by hand, so theirs stays open.
                          (isNotificationField(form.questionText) &&
                            isAutomatedField(form.questionText))
                        }
                        // disabled={this.state.disabled}
                      />
                      {this.state.misMatchFields.includes(form.questionText) &&
                        this.renderChangePrevEntry({
                          field: form.questionText,
                          questionnaireIndex: index,
                          type:
                            form.answerType === "multipleChoice"
                              ? "dropdown"
                              : "",
                          options: options,
                        })}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="modal-footer">
              <Button
                text={language[this.props.defaultLanguage].cancel}
                onPress={() => this.props.handleCloseModal()}
                className="btn-default"
              />
              <Button
                text={
                  this.props?.sampleCollectionMethod === "alinity"
                    ? "Update"
                    : language[this.props.defaultLanguage].submit
                }
                onPress={() => this.onSubmit()}
                isSubmitting={this.state.isSubmitting}
                disabled={this.state.questionnaire.length === 0}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;
  return {
    defaultLanguage,
  };
};

export default connect(mapStateToProps)(Serology);
